import { createClient, StorageApiError } from "@supabase/supabase-js";
import { getEnvVar } from "@/common/utils/environment";
import { withContentType } from "@/common/utils/upload-content-type";

/**
 * Storage access from the browser goes through a grant the API issued after
 * authorizing the user: a signed upload token here, a signed URL for reads.
 * The user's own JWT is never sent to Storage, so no Storage policy has to
 * repeat the API's authorization (docs/storage-direct-access-migration.md).
 *
 * The app's shared client can't be used for this: its fetch adds the signed-in
 * session's JWT to every request, Storage included. This client has no session
 * (`accessToken` resolving to null makes supabase-js send the anon key), so
 * the token alone authorizes the upload, like `tusUploadFile`.
 */
const sessionlessStorage = createClient(
  getEnvVar("VITE_PUBLIC_SUPABASE_URL"),
  getEnvVar("VITE_PUBLIC_SUPABASE_ANON_KEY"),
  { accessToken: async () => null },
).storage;

export type ServerIssuedUpload = {
  bucketName: string;
  storagePath: string;
  token: string;
};

/**
 * Upload one file with a server-issued token. Throws on failure, including
 * Storage's normal `{ error }` result, so callers handle one failure path.
 *
 * `contentType` must be the type the reservation declared. storage-js stores a
 * Blob under the Blob's own type, not the `contentType` option, so the body is
 * re-typed first: a HEIC typed "" would otherwise land as
 * application/octet-stream and fail the scan's check against the reservation.
 */
export async function uploadWithServerToken({
  upload,
  body,
  contentType,
}: {
  upload: ServerIssuedUpload;
  body: Blob;
  contentType: string;
}): Promise<void> {
  const { error } = await sessionlessStorage
    .from(upload.bucketName)
    .uploadToSignedUrl(
      upload.storagePath,
      upload.token,
      withContentType(body, contentType),
      { contentType },
    );
  if (error && !isAlreadyUploaded(error)) {
    throw error;
  }
}

/** Storage's code for a write to a path that already holds an object. */
const OBJECT_ALREADY_EXISTS_STATUS_CODE = "409";

/**
 * Whether the upload failed only because this token already wrote the object.
 *
 * Tokens are issued without overwrite for a fresh path the server built, so
 * an existing object there can only come from an earlier attempt with the same
 * token, one whose response was lost before the client saw it succeed.
 * Retrying such an upload must count as done: replaying it would fail this way
 * forever. Trusting the existing bytes is safe because the scan still compares
 * the stored object with the reservation's size, type and hash.
 */
function isAlreadyUploaded(error: Error): boolean {
  return (
    error instanceof StorageApiError &&
    error.statusCode === OBJECT_ALREADY_EXISTS_STATUS_CODE
  );
}
